import { AxiosError } from 'axios';

/** Which part of a passkey request failed: a call to the vh3-auth Worker, or the browser prompt. */
export type PasskeyStep =
  | 'login/options'
  | 'login/browser'
  | 'login/verify'
  | 'register/options'
  | 'register/browser'
  | 'register/verify'
  | 'list'
  | 'rename'
  | 'remove';

export interface PasskeyFailure {
  step: PasskeyStep;
  /** HTTP status from the Worker; absent when no response arrived. */
  status?: number;
  /** The Worker's `error` code from the response body. */
  code?: string;
  /** Axios code when no response arrived, e.g. ERR_NETWORK (blocked, offline, CORS) or ECONNABORTED (timeout). */
  network?: string;
  /** Error name for browser-side failures, e.g. SecurityError. */
  name?: string;
}

/**
 * What to log when a passkey request fails, so the console says which step broke. Only the step,
 * status and codes: response bodies can carry tokens, so nothing else is read from them.
 */
export function describePasskeyFailure(
  step: PasskeyStep,
  error: unknown
): PasskeyFailure {
  if (error instanceof AxiosError) {
    const body = error.response?.data as { error?: unknown } | undefined;
    return {
      step,
      status: error.response?.status,
      code: typeof body?.error === 'string' ? body.error : undefined,
      network: error.response ? undefined : error.code,
    };
  }
  if (error instanceof Error) return { step, name: error.name };
  return { step };
}
